import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import {
  CustomButton,
  CustomSingleSelect,
  CustomModal,
  type CategoryOption,
} from "@ippon/shared-components";
import { CustomListToolbar } from "../../../../../components/ListToolbar/CustomListToolbar";
import RepairsTable, {
  type RepairRow,
} from "../../../../../components/RepairsTable";
import { Pagination } from "../../../../../components/PropertyList/Pagination";
import RepairCreateDrawer from "../../../../../components/PropertyDetail/PropertyRepairsList/RepairCreateDrawer";
import type { AreaType } from "@ippon/shared-types";
import { AppDispatch } from "@/store";
import { FILTER_FETCH_LIMIT } from "@/constants/listFilter";
import { useFilteredPagination } from "@/hooks/useFilteredPagination";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import { useCan } from "@/hooks/useCan";
import {
  fetchRepairsByProperty,
  deleteRepair,
  patchRepairStatus,
  updateRepairStatusInList,
} from "@/store/repairSlice";
import { fetchCategoryManagement } from "@/store/categoryManagementSlice";

type FilterFormValues = {
  keyword_embed: string;
  category_embed: string;
};

type PerPageForm = { perPage: number };

const initialCategoryOptions: CategoryOption[] = [
  { label: "衛生器具", value: "衛生器具" },
  { label: "外装材", value: "外装材" },
  { label: "躯体等", value: "躯体等" },
  { label: "建具類", value: "建具類" },
  { label: "外部金物等", value: "外部金物等" },
  { label: "自動ドア・シャッター等", value: "自動ドア・シャッター等" },
  { label: "内装", value: "内装" },
  { label: "外構（舗装等）", value: "外構（舗装等）" },
  { label: "変電設備", value: "変電設備" },
  { label: "動力盤、電灯盤、ケーブル等", value: "動力盤、電灯盤、ケーブル等" },
  { label: "電灯コンセント及び配線", value: "電灯コンセント及び配線" },
  { label: "照明設備", value: "照明設備" },
  { label: "電話・TV・放送設備等", value: "電話・TV・放送設備等" },
  { label: "発電機・蓄電池等", value: "発電機・蓄電池等" },
  { label: "空調換気設備", value: "空調換気設備" },
  { label: "受水槽・高架水槽・ポンプ", value: "受水槽・高架水槽・ポンプ" },
  { label: "給水設備", value: "給水設備" },
  { label: "給湯設備", value: "給湯設備" },
  { label: "排水設備", value: "排水設備" },
  { label: "配管設備", value: "配管設備" },
  { label: "昇降機設備（ELV）", value: "昇降機設備（ELV）" },
  { label: "機械式駐車場", value: "機械式駐車場" },
  { label: "消火設備", value: "消火設備" },
  { label: "警報設備", value: "警報設備" },
  { label: "避雷設備", value: "避雷設備" },
  { label: "その他", value: "その他" },
  { label: "屋上防水・屋根", value: "屋上防水・屋根" },
  { label: "防火設備", value: "防火設備" },
  { label: "消防設備点検（複合）", value: "消防設備点検（複合）" },
  { label: "特定建築物定期検査（複合）", value: "特定建築物定期検査（複合）" },
  { label: "防火設備点検（複合）", value: "防火設備点検（複合）" },
  { label: "建築設備定期検査（複合）", value: "建築設備定期検査（複合）" },
  { label: "廃棄物処理", value: "廃棄物処理" },
  {
    label: "非常照明（建築設備、特建、消防）",
    value: "非常照明（建築設備、特建、消防）",
  },
  { label: "誘導灯", value: "誘導灯" },
  { label: "法定点検実施費用", value: "法定点検実施費用" },
  { label: "排煙設備", value: "排煙設備" },
  { label: "リニューアル工事", value: "リニューアル工事" },
  { label: "原状回復工事", value: "原状回復工事" },
];

interface RepairsSectionInlineProps {
  propertyId?: string;
  /** 場所区分の初期値 (private / common / facility / land / other) */
  defaultScope?: AreaType;
  defaultAreaName?: string;
  propertyName?: string;
  privateAreaId?: string;
  privateAreaName?: string;
}

const RepairsSectionInline: React.FC<RepairsSectionInlineProps> = ({
  propertyId,
  defaultScope,
  defaultAreaName,
  propertyName,
  privateAreaId,
  privateAreaName,
}) => {
  const {
    register,
    control,
    formState: { errors },
    setValue,
    getValues,
    watch,
  } = useForm<FilterFormValues>({
    defaultValues: {
      keyword_embed: "",
      category_embed: "",
    },
  });

  const repairsList = useSelector((s: any) => s.repairs.list.data);
  const total = useSelector((s: any) => s.repairs.list.total);
  const loading = useSelector((s: any) => s.repairs.list.loading);
  const categoryRecord = useSelector((s: any) => s.categoryManagement.record);
  const { showToast } = useGlobalToast();
  const dispatch = useDispatch<AppDispatch>();
  const can = useCan();

  const [openRepairModal, setOpenRepairModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedRepairIds, setSelectedRepairIds] = useState<Set<string>>(
    new Set(),
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<{
    keyword: string;
    category: string | null;
  }>({ keyword: "", category: null });
  const hasActiveFilter = !!(appliedFilters.keyword || appliedFilters.category);
  const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>(
    initialCategoryOptions,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(50);

  const { control: perPageControl } = useForm<PerPageForm>({
    defaultValues: { perPage: 50 },
  });

  const perPageOptions = useMemo(
    () => [
      { value: 10, label: "10件" },
      { value: 25, label: "25件" },
      { value: 50, label: "50件" },
      { value: 100, label: "100件" },
    ],
    [],
  );

  useEffect(() => {
    if (!propertyId) return;
    const hasResolvedArea = Boolean(defaultAreaName);
    const shouldFetchWide =
      hasActiveFilter || privateAreaId || !hasResolvedArea;
    dispatch(
      fetchRepairsByProperty({
        property_id: propertyId,
        ...(hasResolvedArea
          ? { area_type: defaultScope, area_name: defaultAreaName }
          : {}),
        page: shouldFetchWide ? 1 : currentPage,
        limit: shouldFetchWide ? FILTER_FETCH_LIMIT : perPage,
      }),
    );
  }, [
    dispatch,
    propertyId,
    defaultScope,
    defaultAreaName,
    privateAreaId,
    currentPage,
    perPage,
    hasActiveFilter,
  ]);

  useEffect(() => {
    dispatch(fetchCategoryManagement() as any)
      .unwrap()
      .then((rec: any) => {
        const mergedValues = Array.from(
          new Set([
            ...initialCategoryOptions.map((o) => o.value),
            ...((rec?.categories as string[]) || []),
          ]),
        );
        setCategoryOptions(
          mergedValues.map((v) => ({ label: String(v), value: String(v) })),
        );
      })
      .catch(() => {
        setCategoryOptions(initialCategoryOptions);
      });
  }, [dispatch]);

  useEffect(() => {
    if (categoryRecord && Array.isArray(categoryRecord.categories)) {
      const mergedValues = Array.from(
        new Set([
          ...initialCategoryOptions.map((o) => o.value),
          ...categoryRecord.categories,
        ]),
      );
      setCategoryOptions(
        mergedValues.map((v) => ({ label: String(v), value: String(v) })),
      );
    }
  }, [categoryRecord]);

  const mappedRows: RepairRow[] = useMemo(() => {
    const items = Array.isArray(repairsList) ? repairsList : [];
    return items.map((r: any) => ({
      id: r.repair_id,
      title: r.repair_title,
      areaType: r.area_type,
      detail: r.area_name,
      expenditureType: r.expenditure_type || "",
      category: Array.isArray(r.category) ? r.category.join(", ") : r.category,
      status: (r.status as any) || "未対応",
      registeredAt: r.created_at,
      estimates: Array.isArray(r.repair_estimates)
        ? r.repair_estimates.map((e: any) => ({
            id: e.repair_estimate_id,
            companyName: e.estimate_company_name || "",
            status: (e.status as any) || "待機中",
            amount: e.estimate_amount ?? null,
            thumbnailUrl: e.repair_image,
          }))
        : [],
      repair_estimates: r.repair_estimates || [],
      repair_images: r.repair_images || [],
      rawData: r,
    }));
  }, [repairsList]);

  const filteredRows: RepairRow[] = useMemo(() => {
    const keyword = (appliedFilters.keyword || "").trim().toLowerCase();
    const category = appliedFilters.category || "";
    return mappedRows.filter((row) => {
      // privateAreaId は unit_id 互換。修繕の target_unit_id 一致のみを表示
      // (旧の area_name 文字列一致から、Unit ID の厳密一致に切替)
      const raw: any = row.rawData ?? {};
      if (privateAreaId) {
        const targetUnitId = raw.target_unit_id ?? raw.target_id ?? "";
        if (targetUnitId && targetUnitId !== privateAreaId) return false;
      }
      const matchKeyword = keyword
        ? (row.title || "").toLowerCase().includes(keyword)
        : true;
      const categoryText = typeof row.category === "string" ? row.category : "";
      const matchCategory = category ? categoryText.includes(category) : true;
      return matchKeyword && matchCategory;
    });
  }, [mappedRows, appliedFilters, privateAreaId]);

  const { paginatedRows, totalPages } = useFilteredPagination({
    hasActiveFilter,
    rows: filteredRows,
    serverTotal: total || 0,
    currentPage,
    perPage,
  });

  const handlePageChange = (page: number) => setCurrentPage(page);

  const handlePerPageChange = (value: number) => {
    setPerPage(value);
    setCurrentPage(1);
  };

  const handlePatchStatus = async (
    id: string | number,
    status: "未対応" | "進行中" | "完了" | "支払い済",
  ) => {
    const repairId = String(id);
    const previousItem = Array.isArray(repairsList)
      ? repairsList.find(
          (r: { repair_id: string; status?: string }) =>
            r.repair_id === repairId,
        )
      : undefined;
    const previousStatus = previousItem?.status;

    dispatch(updateRepairStatusInList({ repair_id: repairId, status }));

    try {
      await dispatch(
        patchRepairStatus({ repair_id: repairId, status }),
      ).unwrap();
      showToast({ message: "ステータスを更新しました", type: "success" });
    } catch (e) {
      console.error("工事ステータス更新に失敗しました", e);
      if (previousStatus !== undefined) {
        dispatch(
          updateRepairStatusInList({
            repair_id: repairId,
            status: previousStatus,
          }),
        );
      }
      showToast({ message: "ステータス更新に失敗しました", type: "error" });
      throw e;
    }
  };

  const handleDeleteRepairs = async () => {
    setOpenDeleteModal(false);
    setIsDeleting(true);
    try {
      const deletePromises = Array.from(selectedRepairIds).map(
        async (repairId) => {
          const action = await dispatch(
            deleteRepair({ repair_id: repairId }) as any,
          );
          return action;
        },
      );
      const results = await Promise.all(deletePromises);
      const successfulResults = results.filter(
        (r) => r.type === "repairs/delete/fulfilled",
      );
      const failedResults = results.filter(
        (r) => r.type === "repairs/delete/rejected",
      );
      if (successfulResults.length === results.length) {
        showToast({
          type: "success",
          message: `${selectedRepairIds.size}件の修繕を削除しました`,
        });
        setSelectedRepairIds(new Set());
        if (propertyId && defaultAreaName)
          dispatch(
            fetchRepairsByProperty({
              property_id: propertyId,
              area_type: defaultScope,
              area_name: defaultAreaName,
              page: currentPage,
              limit: perPage,
            }),
          );
      } else {
        const errorMessages = failedResults.map((result) => {
          const error = result.payload as any;
          return error?.message || "削除に失敗しました";
        });
        showToast({
          type: "error",
          message: `${failedResults.length}件の修繕の削除に失敗しました: ${errorMessages.join(", ")}`,
        });
      }
    } catch (error) {
      console.error("Delete error:", error);
      showToast({
        type: "error",
        message: "工事の削除中にエラーが発生しました",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSearch = () => {
    const kw = (getValues("keyword_embed") || "").toString();
    const catValue = getValues("category_embed");
    const category =
      typeof catValue === "string"
        ? catValue
        : ((catValue as { value?: string } | undefined)?.value ?? "");
    setAppliedFilters({ keyword: kw, category: category || null });
    setCurrentPage(1);
  };

  const handleClear = () => {
    setValue("keyword_embed", "");
    setValue("category_embed", "");
    setAppliedFilters({ keyword: "", category: null });
    setCurrentPage(1);
  };

  const handleKeywordClear = () => {
    setValue("keyword_embed", "");
    setAppliedFilters((prev) => ({ ...prev, keyword: "" }));
    setCurrentPage(1);
  };

  return (
    <>
      <Box sx={{ mb: 1 }}>
        <CustomListToolbar
          searchValue={watch("keyword_embed")}
          onSearchValueClear={handleKeywordClear}
          hasActiveFilters={!!watch("category_embed")}
          hidePerPage={true}
          responsiveBreakpoint="md"
          showSearch={true}
          searchPlaceholder="工事名で絞り込む"
          searchRegister={register as any}
          searchErrors={errors}
          searchName="keyword_embed"
          onSearch={handleSearch}
          onClear={handleClear}
          leftExtra={
            // 工事一覧 (PropertyRepairsList) と同じく幅を固定ラップする。
            // 無いと inputWidth="100%" がツールバー内で伸び切り、画面幅で崩れる。
            <Box sx={{ width: { xs: "100%", md: 140 } }}>
              <CustomSingleSelect
                label=""
                placeholder="カテゴリ名"
                name="category_embed"
                control={control as any}
                options={categoryOptions}
                autoMenuWidth
                labelWidth="0"
                labelWidthSp="0"
                inputWidth="100%"
                inputWidthSp="100%"
                hasSpacing={false}
              />
            </Box>
          }
          perPageOptions={perPageOptions}
          perPageControl={perPageControl}
          onPerPageChange={handlePerPageChange}
          primaryButtonLabel="工事登録"
          primaryButtonOnClick={() => setOpenRepairModal(true)}
          showPrimaryButton={can("repair:edit")}
          deleteDisabled={isDeleting || selectedRepairIds.size === 0}
          onDeleteClick={() => {
            if (selectedRepairIds.size === 0) {
              showToast({
                type: "warning",
                message: "削除する修繕を選択してください",
              });
              return;
            }
            setOpenDeleteModal(true);
          }}
          showDelete={can("repair:delete")}
        />
      </Box>

      <RepairsTable
        rows={paginatedRows}
        onSelectionChange={(ids) =>
          setSelectedRepairIds(new Set(ids.map(String)))
        }
        onPatchStatus={handlePatchStatus}
        hideAreaType
        hideDetail
        loading={loading}
        propertyId={propertyId}
        propertyName={propertyName}
        fromSource="private-area"
        privateAreaId={privateAreaId}
        privateAreaName={privateAreaName}
        pagination={
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        }
      />

      {propertyId && (
        <RepairCreateDrawer
          open={openRepairModal}
          onClose={() => setOpenRepairModal(false)}
          propertyId={propertyId}
          initialScope={defaultScope}
          initialAreaId={privateAreaId}
          onCreated={() => {
            if (propertyId && defaultAreaName) {
              dispatch(
                fetchRepairsByProperty({
                  property_id: propertyId,
                  area_type: defaultScope,
                  area_name: defaultAreaName,
                  page: currentPage,
                  limit: perPage,
                }),
              );
            }
          }}
        />
      )}

      <CustomModal
        title="削除の確認"
        openModal={openDeleteModal}
        handleCloseModal={() => setOpenDeleteModal(false)}
        bodyText=""
        hideCloseButton
        modalType="deleteModal"
      >
        <Box>
          <Typography sx={{ fontSize: "14px", color: "#666" }}>
            選択した{selectedRepairIds.size}
            件の修繕情報を削除してもよろしいでしょうか？
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center", gap: "10px" }}>
          <CustomButton
            label="戻る"
            buttonCategory="cancelModal"
            onClick={() => setOpenDeleteModal(false)}
          />
          <CustomButton
            label="削除"
            onClick={handleDeleteRepairs}
            buttonCategory="deleteModal"
            disabled={isDeleting}
          />
        </Box>
      </CustomModal>
    </>
  );
};

export default RepairsSectionInline;
